import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  bigint,
  numeric,
  boolean,
  jsonb,
  index,
  uniqueIndex,
  unique,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// JSONB type interfaces (inline to avoid cross-package dependency)
// ---------------------------------------------------------------------------

export interface CompanyAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  [key: string]: unknown;
}

export interface ViewFilter {
  field: string;
  operator: string;
  value: unknown;
}

export interface ViewSort {
  field: string;
  direction: "asc" | "desc";
}

export interface CustomFieldOption {
  value: string;
  label: string;
  color?: string;
}

// ---------------------------------------------------------------------------
// 1. People
// ---------------------------------------------------------------------------

export const people = pgTable(
  "people",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    email: text("email").notNull(),
    emailHash: text("email_hash").notNull(),
    firstName: text("first_name"),
    lastName: text("last_name"),
    phone: text("phone"),
    timezone: text("timezone"),
    avatarUrl: text("avatar_url"),
    tags: text("tags").array(),
    source: text("source"),
    sourceDetail: text("source_detail"),
    customFields: jsonb("custom_fields")
      .$type<Record<string, unknown>>()
      .default({}),
    leadScore: integer("lead_score").default(0).notNull(),
    engagementStatus: text("engagement_status").default("new").notNull(),
    lifecycleStage: text("lifecycle_stage").default("subscriber").notNull(),
    lastActivityAt: timestamp("last_activity_at", tz),
    emailVerifiedAt: timestamp("email_verified_at", tz),
    // F1: External system ID for migration dedup (GHL, HubSpot, ActiveCampaign, CSV)
    externalId: text("external_id"),
    // F13: Churn probability (0-100) from predictive model. Null = not yet scored.
    churnScore: integer("churn_score"),
    // F3: Cumulative revenue in cents. bigint: B2B lifetime values can exceed $21M (integer overflow).
    lifetimeValueCents: bigint("lifetime_value_cents", { mode: "number" }),
    // S.7: Event-agnostic qualification scoring
    qualificationScore: integer("qualification_score"), // 0-100, accumulated from qualifying events
    qualificationTier: text("qualification_tier"), // "Hot" | "Warm" | "Cold" | custom
    signalBreakdown: jsonb("signal_breakdown").$type<Record<string, number>>(), // { "Intent": 35, "Budget": 25 }
    // S.9: Lifecycle journey tracking (distinct from pipeline stages)
    journeyStage: text("journey_stage").default("awareness"),
    // "awareness" | "consideration" | "purchase" | "onboarding" | "active" | "renewal" | "churned"
    // S.10: Engagement trend detection (PI.1 materializer computes from 7d vs 30d rolling)
    engagementVelocity: text("engagement_velocity"), // "increasing" | "stable" | "declining"
    engagementVelocityComputedAt: timestamp("engagement_velocity_computed_at", tz),

    // EM-012: Consent-aware email tracking (EU/ePrivacy + GDPR Art. 25).
    // NULL = unknown (treat as no consent -> suppress, fail-closed default).
    // TRUE = contact granted tracking consent (opens/clicks may be recorded).
    // FALSE = contact denied/withdrew tracking consent (events MUST be suppressed).
    // Authoritative DB source for the per-contact check; complements platform_consent_records.
    trackingConsent: boolean("tracking_consent"),
    trackingConsentUpdatedAt: timestamp("tracking_consent_updated_at", tz),

    piiClassification: jsonb("pii_classification"),
    encryptionKeyId: text("encryption_key_id"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
    deletedAt: timestamp("deleted_at", tz),
  },
  (table) => [
    index("idx_people_tenant").on(table.tenantId),
    // Partial unique index: only active (non-deleted) people enforce email uniqueness.
    // Allows re-registration after soft-delete. Defined via raw SQL in migration 0007.
    // Drizzle doesn't support WHERE clause on uniqueIndex, so this is a documentation marker.
    index("idx_people_email_hash").on(table.tenantId, table.emailHash),
    // Partial unique: UNIQUE(tenant_id, external_id) WHERE external_id IS NOT NULL AND deleted_at IS NULL
    // Drizzle doesn't support WHERE on uniqueIndex — defined via raw SQL in migration.
    index("idx_people_external_id").on(table.tenantId, table.externalId),
    index("idx_people_tags").using("gin", table.tags),
    index("idx_people_lead_score").on(table.tenantId, table.leadScore),
    index("idx_people_lifecycle").on(table.tenantId, table.lifecycleStage),
    index("idx_people_last_activity").on(table.tenantId, table.lastActivityAt),
    index("idx_people_engagement").on(table.tenantId, table.engagementStatus),
  ],
);

// ---------------------------------------------------------------------------
// 1b. Email Engagement Profiles
// ---------------------------------------------------------------------------

export const emailEngagementProfiles = pgTable(
  "email_engagement_profiles",
  {
    personId: uuid("person_id").primaryKey(),
    tenantId: uuid("tenant_id").notNull(),
    emailsSent30d: integer("emails_sent_30d").default(0).notNull(),
    emailsOpened30d: integer("emails_opened_30d").default(0).notNull(),
    emailsClicked30d: integer("emails_clicked_30d").default(0).notNull(),
    lastEmailSentAt: timestamp("last_email_sent_at", tz),
    lastEmailOpenedAt: timestamp("last_email_opened_at", tz),
    lastEmailClickedAt: timestamp("last_email_clicked_at", tz),
    totalRevenue: numeric("total_revenue", { precision: 19, scale: 4 }).default("0"),
    lastPurchaseAt: timestamp("last_purchase_at", tz),
    engagementScore: integer("engagement_score").default(0).notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    index("idx_email_engagement_tenant").on(table.tenantId),
  ],
);

// ---------------------------------------------------------------------------
// 2. Companies
// ---------------------------------------------------------------------------

export const companies = pgTable(
  "companies",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    domain: text("domain"),
    industry: text("industry"),
    size: text("size"),
    website: text("website"),
    address: jsonb("address").$type<CompanyAddress>(),
    annualRevenue: numeric("annual_revenue"),
    customFields: jsonb("custom_fields")
      .$type<Record<string, unknown>>()
      .default({}),
    ownerId: text("owner_id"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
    deletedAt: timestamp("deleted_at", tz),
  },
  (table) => [
    index("idx_companies_tenant").on(table.tenantId),
    index("idx_companies_domain").on(table.tenantId, table.domain),
  ],
);

// ---------------------------------------------------------------------------
// 3. Contacts (person <-> company join)
// ---------------------------------------------------------------------------

export const crmContacts = pgTable(
  "crm_contacts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    personId: uuid("person_id").notNull(),
    companyId: uuid("company_id").notNull(),
    role: text("role"),
    isPrimary: boolean("is_primary").notNull().default(false),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_contacts_person").on(table.tenantId, table.personId),
    index("idx_contacts_company").on(table.tenantId, table.companyId),
    uniqueIndex("idx_crm_contacts_company_person_unique").on(table.tenantId, table.companyId, table.personId),
  ],
);

// ---------------------------------------------------------------------------
// 4. Deals
// ---------------------------------------------------------------------------

export const deals = pgTable(
  "deals",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    pipelineId: text("pipeline_id").notNull(),
    stageId: text("stage_id").notNull(),
    personId: uuid("person_id"),
    companyId: uuid("company_id"),
    title: text("title").notNull(),
    value: numeric("value"),
    currency: text("currency").notNull().default("EUR"),
    expectedCloseDate: timestamp("expected_close_date", tz),
    closedAt: timestamp("closed_at", tz),
    lostReason: text("lost_reason"),
    // S.4: Structured loss tracking for win/loss analysis
    lossCompetitor: text("loss_competitor"),
    winReason: text("win_reason"),
    score: integer("score").notNull().default(0),
    // S.4: Deal intelligence for forecasting
    forecastCategory: text("forecast_category"), // "commit" | "best_case" | "pipeline" | "omitted"
    winProbability: integer("win_probability"), // 0-100, from stage default, adjusted by activity
    ownerId: text("owner_id"),
    customFields: jsonb("custom_fields")
      .$type<Record<string, unknown>>()
      .default({}),
    lastHumanActivityAt: timestamp("last_human_activity_at", tz),
    // Sprint 7 A1: Deal momentum scoring
    momentumDirection: text("momentum_direction"), // "accelerating" | "stable" | "decelerating"
    momentumScore: integer("momentum_score"), // 0-100 composite momentum
    momentumComputedAt: timestamp("momentum_computed_at", tz),
    stageEnteredAt: timestamp("stage_entered_at", tz).notNull().defaultNow(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
    deletedAt: timestamp("deleted_at", tz),
  },
  (table) => [
    index("idx_deals_tenant").on(table.tenantId),
    index("idx_deals_pipeline_stage").on(
      table.tenantId,
      table.pipelineId,
      table.stageId,
    ),
    index("idx_deals_person").on(table.tenantId, table.personId),
    index("idx_deals_company").on(table.tenantId, table.companyId),
  ],
);

// ---------------------------------------------------------------------------
// 5. Stage Transitions
// ---------------------------------------------------------------------------

export const stageTransitions = pgTable(
  "stage_transitions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    dealId: uuid("deal_id").notNull(),
    fromStage: text("from_stage").notNull(),
    toStage: text("to_stage").notNull(),
    reason: text("reason"),
    transitionedBy: text("transitioned_by").notNull(),
    transitionSource: text("transition_source").notNull(),
    timeInPreviousStageSeconds: integer("time_in_previous_stage_seconds"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_stage_transitions_deal").on(table.tenantId, table.dealId),
  ],
);

// ---------------------------------------------------------------------------
// 6. Activities
// ---------------------------------------------------------------------------

export const activities = pgTable(
  "activities",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    type: text("type").notNull(),
    subject: text("subject"),
    body: text("body"),
    personId: uuid("person_id"),
    companyId: uuid("company_id"),
    dealId: uuid("deal_id"),
    direction: text("direction"),
    durationSeconds: integer("duration_seconds"),
    outcome: text("outcome"),
    isAutomated: boolean("is_automated").notNull().default(false),
    metadata: jsonb("metadata")
      .$type<Record<string, unknown>>()
      .default({}),
    performedBy: text("performed_by"),
    performedAt: timestamp("performed_at", tz).notNull().defaultNow(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    deletedAt: timestamp("deleted_at", tz),
  },
  (table) => [
    index("idx_activities_tenant").on(table.tenantId),
    index("idx_activities_person").on(table.tenantId, table.personId),
    index("idx_activities_deal").on(table.tenantId, table.dealId),
    index("idx_activities_performed_at").on(
      table.tenantId,
      table.performedAt,
    ),
  ],
);

// ---------------------------------------------------------------------------
// 7. Tasks
// ---------------------------------------------------------------------------

export const crmTasks = pgTable(
  "crm_tasks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    title: text("title").notNull(),
    description: text("description"),
    type: text("type").notNull(),
    priority: text("priority").notNull().default("medium"),
    status: text("status").notNull().default("pending"),
    personId: uuid("person_id"),
    dealId: uuid("deal_id"),
    companyId: uuid("company_id"),
    offerId: text("offer_id"),
    assignedTo: text("assigned_to"),
    dueAt: timestamp("due_at", tz),
    completedAt: timestamp("completed_at", tz),
    completedBy: text("completed_by"),
    source: text("source").notNull().default("manual"),
    sourceRuleId: text("source_rule_id"),
    template: text("template"),
    notes: text("notes"),

    // SD.8: Task outcome tracking + context
    outcome: text("outcome"), // completed/rescheduled/no_answer/not_interested/callback_requested
    outcomeNotes: text("outcome_notes"),
    nextAction: text("next_action"),
    followUpDate: timestamp("follow_up_date", tz),
    contextSnapshot: jsonb("context_snapshot").$type<{
      leadScore?: number;
      dealValue?: number;
      lastActivity?: string;
      healthScore?: number;
      creditBalance?: number;
    }>(),
    scriptTemplate: text("script_template"), // guidance text for staff
    triggerType: text("trigger_type"), // health_score_drop, balance_low, no_show, inactivity
    triggerEventId: uuid("trigger_event_id"),

    metadata: jsonb("metadata")
      .$type<Record<string, unknown>>()
      .default({}),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_tasks_tenant").on(table.tenantId),
    index("idx_tasks_assigned").on(
      table.tenantId,
      table.assignedTo,
      table.status,
    ),
    index("idx_tasks_person").on(table.tenantId, table.personId),
    index("idx_tasks_deal").on(table.tenantId, table.dealId),
    index("idx_tasks_due")
      .on(table.tenantId, table.dueAt)
      .where(sql`status = 'pending'`),
    index("idx_tasks_follow_up")
      .on(table.tenantId, table.followUpDate)
      .where(sql`follow_up_date IS NOT NULL AND status = 'pending'`),
  ],
);

export const crmTaskEvents = pgTable(
  "crm_task_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    taskId: uuid("task_id").notNull(),
    eventType: text("event_type").notNull(),
    eventSource: text("event_source").notNull().default("system"),
    actorId: text("actor_id"),
    actorType: text("actor_type"),
    previousStatus: text("previous_status"),
    nextStatus: text("next_status"),
    previousAssignee: text("previous_assignee"),
    nextAssignee: text("next_assignee"),
    outcome: text("outcome"),
    reason: text("reason"),
    notes: text("notes"),
    triggerEventType: text("trigger_event_type"),
    triggerEventId: text("trigger_event_id"),
    metadata: jsonb("metadata")
      .$type<Record<string, unknown>>()
      .default({}),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_task_events_tenant_created").on(table.tenantId, table.createdAt),
    index("idx_task_events_task_created").on(table.tenantId, table.taskId, table.createdAt),
    index("idx_task_events_type").on(table.tenantId, table.eventType),
    index("idx_task_events_trigger").on(table.tenantId, table.triggerEventType, table.triggerEventId),
  ],
);

// ---------------------------------------------------------------------------
// Form-originated CRM action idempotency
// ---------------------------------------------------------------------------

export const crmFormActionIdempotency = pgTable(
  "crm_form_action_idempotency",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    submissionId: text("submission_id").notNull(),
    targetType: text("target_type").notNull(),
    targetId: uuid("target_id"),
    status: text("status").default("claimed").notNull(),
    metadata: jsonb("metadata")
      .$type<Record<string, unknown>>()
      .default({})
      .notNull(),
    errorMessage: text("error_message"),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_crm_form_action_idempotency_action").on(
      table.tenantId,
      table.submissionId,
      table.targetType,
    ),
    index("idx_crm_form_action_idempotency_tenant_status").on(
      table.tenantId,
      table.status,
    ),
  ],
);

// ---------------------------------------------------------------------------
// 8. CRM Events (event sourcing log)
// ---------------------------------------------------------------------------

export const crmEvents = pgTable(
  "crm_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    entityType: text("entity_type").notNull(),
    entityId: uuid("entity_id").notNull(),
    eventType: text("event_type").notNull(),
    eventSource: text("event_source").notNull(),
    payload: jsonb("payload")
      .$type<Record<string, unknown>>()
      .notNull()
      .default({}),
    schemaVersion: integer("schema_version").notNull().default(1),
    actorId: text("actor_id").notNull(),
    actorType: text("actor_type").notNull(),
    sequenceNumber: integer("sequence_number").notNull(),
    causationId: uuid("causation_id"),
    correlationId: uuid("correlation_id"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_crm_events_entity").on(
      table.tenantId,
      table.entityId,
      table.sequenceNumber,
    ),
    index("idx_crm_events_type").on(table.tenantId, table.eventType),
    unique("uq_crm_events_entity_sequence").on(
      table.tenantId,
      table.entityId,
      table.sequenceNumber,
    ),
  ],
);

// ---------------------------------------------------------------------------
// 9. Event Handler Log
// ---------------------------------------------------------------------------

export const eventHandlerLog = pgTable(
  "event_handler_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    eventId: uuid("event_id").notNull(),
    handlerName: text("handler_name").notNull(),
    status: text("status").notNull(),
    errorMessage: text("error_message"),
    retries: integer("retries").notNull().default(0),
    lastAttemptAt: timestamp("last_attempt_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_event_handler_log_event").on(table.eventId),
  ],
);

// ---------------------------------------------------------------------------
// 10. Entity Associations
// ---------------------------------------------------------------------------

export const entityAssociations = pgTable(
  "entity_associations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    entityAType: text("entity_a_type").notNull(),
    entityAId: uuid("entity_a_id").notNull(),
    entityBType: text("entity_b_type").notNull(),
    entityBId: uuid("entity_b_id").notNull(),
    label: text("label").notNull(),
    metadata: jsonb("metadata").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_associations_a").on(
      table.tenantId,
      table.entityAType,
      table.entityAId,
    ),
    index("idx_associations_b").on(
      table.tenantId,
      table.entityBType,
      table.entityBId,
    ),
  ],
);

// ---------------------------------------------------------------------------
// 11. Unified Custom Field Definitions
// ---------------------------------------------------------------------------

export const unifiedCustomFieldDefinitions = pgTable(
  "unified_custom_field_definitions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    entityType: text("entity_type").notNull(),
    fieldKey: text("field_key").notNull(),
    fieldLabel: text("field_label").notNull(),
    fieldType: text("field_type").notNull(),
    options: jsonb("options"),
    required: boolean("required").default(false).notNull(),
    engines: text("engines").array().default(["crm"]).notNull(),
    showInList: boolean("show_in_list").default(false).notNull(),
    showInCard: boolean("show_in_card").default(true).notNull(),
    displayOrder: integer("display_order").default(0).notNull(),
    validation: jsonb("validation"),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_custom_field_tenant_entity_key").on(
      table.tenantId,
      table.entityType,
      table.fieldKey,
    ),
    index("idx_custom_field_engines").using("gin", table.engines),
  ],
);

// ---------------------------------------------------------------------------
// 12. Saved Views
// ---------------------------------------------------------------------------

export const savedViews = pgTable(
  "saved_views",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    entityType: text("entity_type").notNull(),
    filters: jsonb("filters").$type<ViewFilter[]>(),
    sort: jsonb("sort").$type<ViewSort[]>(),
    columns: jsonb("columns").$type<string[]>(),
    grouping: jsonb("grouping").$type<Record<string, unknown>>(),
    isDefault: boolean("is_default").notNull().default(false),
    isShared: boolean("is_shared").notNull().default(false),
    ownerId: text("owner_id").notNull(),
    pinOrder: integer("pin_order"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_views_owner").on(
      table.tenantId,
      table.ownerId,
      table.entityType,
    ),
  ],
);

// ---------------------------------------------------------------------------
// 13. Duplicate Candidates
// ---------------------------------------------------------------------------

export const duplicateCandidates = pgTable(
  "duplicate_candidates",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    entityType: text("entity_type").notNull(),
    entityAId: uuid("entity_a_id").notNull(),
    entityBId: uuid("entity_b_id").notNull(),
    confidence: numeric("confidence").notNull(),
    matchReasons: jsonb("match_reasons").$type<string[]>().notNull(),
    status: text("status").notNull().default("pending"),
    resolvedBy: text("resolved_by"),
    resolvedAt: timestamp("resolved_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_duplicates_entity").on(
      table.tenantId,
      table.entityType,
      table.status,
    ),
  ],
);

// ---------------------------------------------------------------------------
// 14. Merge History
// ---------------------------------------------------------------------------

export const mergeHistory = pgTable(
  "merge_history",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    winnerId: uuid("winner_id").notNull(),
    loserId: uuid("loser_id").notNull(),
    mergedFields: jsonb("merged_fields"),
    mergedBy: text("merged_by").notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_merge_history_loser").on(table.tenantId, table.loserId),
    index("idx_merge_history_winner").on(table.tenantId, table.winnerId),
  ],
);

// ---------------------------------------------------------------------------
// 15. Contact Channel Profiles — per-channel engagement tracking
// ---------------------------------------------------------------------------

export const contactChannelProfiles = pgTable(
  "contact_channel_profiles",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    contactId: uuid("contact_id").notNull(),
    channel: text("channel").notNull(), // email, sms, whatsapp, push
    totalSent: integer("total_sent").notNull().default(0),
    totalDelivered: integer("total_delivered").notNull().default(0),
    totalOpened: integer("total_opened").notNull().default(0),
    totalClicked: integer("total_clicked").notNull().default(0),
    totalBounced: integer("total_bounced").notNull().default(0),
    lastSentAt: timestamp("last_sent_at", tz),
    lastOpenedAt: timestamp("last_opened_at", tz),
    lastClickedAt: timestamp("last_clicked_at", tz),
    engagementScore: integer("engagement_score").notNull().default(0),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    unique("uq_channel_profile_tenant_contact_channel").on(
      table.tenantId,
      table.contactId,
      table.channel,
    ),
    index("idx_channel_profile_tenant_contact").on(
      table.tenantId,
      table.contactId,
    ),
    index("idx_channel_profile_engagement").on(
      table.tenantId,
      table.channel,
      table.engagementScore,
    ),
  ],
);

// ---------------------------------------------------------------------------
// 16. Person Channel Identifiers — maps platform-specific IDs to unified person
// ---------------------------------------------------------------------------

/** Maps platform-specific IDs to unified person record across channels */
export const personChannelIdentifiers = pgTable(
  "person_channel_identifiers",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    personId: uuid("person_id").notNull(),
    channel: text("channel").notNull(),  // whatsapp, instagram_dm, facebook_messenger, sms
    channelUserId: text("channel_user_id").notNull(),  // WAID, IGSID, PSID, phone
    verifiedAt: timestamp("verified_at", tz),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_person_channel_tenant").on(table.tenantId, table.channel, table.channelUserId),
    index("idx_person_channel_person").on(table.tenantId, table.personId),
  ],
);

// ---------------------------------------------------------------------------
// 17. Meta Page Token Registry — maps pageId to tenant for webhook routing
// ---------------------------------------------------------------------------

/** Meta page token registry — maps pageId to tenant for webhook routing */
export const metaPageTokens = pgTable(
  "meta_page_tokens",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    pageId: text("page_id").notNull(),
    channel: text("channel").notNull(),  // instagram_dm, facebook_messenger, whatsapp
    encryptedPageAccessToken: text("encrypted_page_access_token").notNull(),
    appId: text("app_id").notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_meta_page_token").on(table.pageId, table.channel),
    index("idx_meta_page_tenant").on(table.tenantId),
  ],
);
